import React, { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useGetTPPChangeLogQuery } from '../queries/useGetTPPChangeLogQuery'
import { useGetTPPColumnVersionsQuery } from '../queries/useGetTPPColumnVersionsQuery'
import ChangeEntryGroup from './ChangeEntryGroup'
import VersionMilestone from './VersionMilestone'
import { GroupedChange, ColumnVersion } from '../types'

type TimelineItem =
    | { type: 'change'; data: GroupedChange; timestamp: number }
    | { type: 'version'; data: ColumnVersion; timestamp: number }

interface ChangeLogContentProps {
    tppId: string
    columnId: string | null
    mockData?: GroupedChange[]
}

const ChangeLogContent = ({ tppId, columnId, mockData }: ChangeLogContentProps) => {
    const {
        data: fetchedData,
        isLoading: isLoadingChanges,
        isError: isErrorChanges,
    } = useGetTPPChangeLogQuery(
        mockData ? null : tppId, // Skip query if mockData provided
        { columnId: columnId || undefined }
    )

    const { data: versionsData, isLoading: isLoadingVersions } = useGetTPPColumnVersionsQuery(
        mockData ? null : columnId
    )

    const isLoading = isLoadingChanges || isLoadingVersions
    const isError = isErrorChanges

    // Filter mock data by columnId (real data is filtered by the API)
    const filteredMockData = useMemo(() => {
        if (!mockData || !columnId) return mockData

        return mockData
            .map((group) => ({
                ...group,
                changes: group.changes.filter((change) => change.columnId === columnId),
            }))
            .filter((group) => group.changes.length > 0)
    }, [mockData, columnId])

    const groupedChanges = filteredMockData || fetchedData

    // Merge changes and versions into a single timeline, sorted by date (newest first)
    // Version saves act as "breaks" that split change groups
    const timelineItems = useMemo<TimelineItem[]>(() => {
        const items: TimelineItem[] = []

        // Get version save timestamps for splitting change groups
        const versionTimestamps = (versionsData?.versions || [])
            .map((v) => new Date(v.createdAt).getTime())
            .sort((a, b) => b - a) // newest first

        // Add change groups, splitting them if a version save occurred in between
        if (groupedChanges) {
            for (const group of groupedChanges) {
                // Get timestamps of changes in this group
                const changes = group.changes || []
                if (changes.length === 0) continue

                // Check if any version saves fall within this group's time range
                const changeTimestamps = changes.map((c) => new Date(c.time).getTime())
                const groupStart = Math.min(...changeTimestamps)
                const groupEnd = Math.max(...changeTimestamps)

                // Find version saves that fall within this group's range
                const splittingVersions = versionTimestamps.filter((vt) => vt >= groupStart && vt <= groupEnd)

                if (splittingVersions.length === 0) {
                    // No version saves in this range, add group as-is
                    items.push({
                        type: 'change',
                        data: group,
                        timestamp: groupEnd,
                    })
                } else {
                    // Split the group at version save points
                    const sortedChanges = [...changes].sort(
                        (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
                    )

                    let currentSubgroup: typeof changes = []
                    let lastVersionTime = Infinity

                    for (const change of sortedChanges) {
                        const changeTime = new Date(change.time).getTime()

                        // Check if we crossed a version save boundary
                        const crossedVersion = splittingVersions.find((vt) => changeTime < vt && vt < lastVersionTime)

                        if (crossedVersion && currentSubgroup.length > 0) {
                            // Save current subgroup before the version
                            items.push({
                                type: 'change',
                                data: {
                                    ...group,
                                    changes: currentSubgroup,
                                },
                                timestamp: new Date(currentSubgroup[0].time).getTime(),
                            })
                            currentSubgroup = []
                            lastVersionTime = crossedVersion
                        }

                        currentSubgroup.push(change)
                    }

                    // Add remaining changes
                    if (currentSubgroup.length > 0) {
                        items.push({
                            type: 'change',
                            data: {
                                ...group,
                                changes: currentSubgroup,
                            },
                            timestamp: new Date(currentSubgroup[0].time).getTime(),
                        })
                    }
                }
            }
        }

        // Add version saves
        if (versionsData?.versions) {
            for (const version of versionsData.versions) {
                items.push({
                    type: 'version',
                    data: version,
                    timestamp: new Date(version.createdAt).getTime(),
                })
            }
        }

        // Sort by timestamp, newest first
        items.sort((a, b) => b.timestamp - a.timestamp)

        return items
    }, [groupedChanges, versionsData])

    if (isLoading && !mockData) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center">
                <CircularProgress size={40} />
            </Stack>
        )
    }

    if (isError && !mockData) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center">
                <Typography variant="16x400" color="error">
                    Error loading change log. Please try again.
                </Typography>
            </Stack>
        )
    }

    if (timelineItems.length === 0) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center">
                <Typography variant="16x400" color="figma.grayscale.textHier2">
                    No changes recorded yet.
                </Typography>
            </Stack>
        )
    }

    return (
        <Box
            sx={{
                position: 'relative',
                flex: 1,
                minHeight: 0,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'auto',
                    pr: 1,
                }}
            >
                <Stack gap={3}>
                    {timelineItems.map((item, index) => {
                        if (item.type === 'version') {
                            return (
                                <React.Fragment key={`version-${item.data._id}`}>
                                    <VersionMilestone version={item.data} />
                                    <Divider sx={{ borderColor: 'figma.grayscale.stroke' }} />
                                </React.Fragment>
                            )
                        }
                        return (
                            <ChangeEntryGroup
                                key={`change-${item.data.date}-${item.data.user?._id || index}`}
                                group={item.data}
                            />
                        )
                    })}
                </Stack>
            </Box>
        </Box>
    )
}

export default ChangeLogContent
